import React from 'react';
import { ScrollView, RefreshControl, ActivityIndicator, View } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Text } from '@/components/ui/text';
import {
  AdminPageHeader,
  AdminTabBar,
  AdminSectionCard,
  ADMIN_COLORS,
  type AdminTab,
} from '../../components/admin/AdminUI';
import { isSuperAdmin } from '../../utils/roleUtils';
import { useTranslation } from '../../hooks/useTranslation';
import FeedbackManagementScreen from './FeedbackManagementScreen';

import type { PlatformAdminScreenProps, PlatformAdminTabKey } from './platform/platformAdminTypes';
import { usePlatformAdminData } from './platform/hooks/usePlatformAdminData';
import { usePlatformAdminTabs } from './platform/hooks/usePlatformAdminTabs';
import { usePlatformQuickActions } from './platform/hooks/usePlatformQuickActions';
import { useSchoolActions } from './platform/hooks/useSchoolActions';
import { useUserActions } from './platform/hooks/useUserActions';
import OverviewTab from './platform/tabs/OverviewTab';
import SchoolsTab from './platform/tabs/SchoolsTab';
import UsersTab from './platform/tabs/UsersTab';
import SettingsTab from './platform/tabs/SettingsTab';
import SchoolActionsModal from './platform/modals/SchoolActionsModal';
import SchoolInviteAndPlanModals from './platform/modals/SchoolInviteAndPlanModals';

/**
 * Platform administration screen (super admins only).
 *
 * A thin shell: data, navigation and actions live in hooks under
 * ./platform/hooks, each tab is a component under ./platform/tabs, and the
 * Settings cards are listed per section under ./platform/settings.
 */
export default function PlatformAdminScreenEnhanced({ user }: PlatformAdminScreenProps) {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();

  const {
    loading,
    refreshing,
    schools,
    stats,
    platformUsers,
    adminsBySchool,
    chatStreaming,
    analysisStreaming,
    agenticChat,
    loadPlatformData,
    onRefresh,
    resendAdminInvite,
  } = usePlatformAdminData(t);

  const {
    activeTab,
    handleTabChange,
    settingsSection,
    setSettingsSection,
    focusedSchoolId,
    focusSchoolInSettings,
  } = usePlatformAdminTabs();

  const schoolActions = useSchoolActions(t, loadPlatformData);
  const { setShowAdminModal, schoolSettingsMenu, handleOpenSchoolSettings } = schoolActions;
  const userActions = useUserActions(t, user, loadPlatformData);
  const quickActions = usePlatformQuickActions(loadPlatformData);

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab stats={stats} quickActions={quickActions} />;

      case 'schools':
        return (
          <SchoolsTab
            schools={schools}
            adminsBySchool={adminsBySchool}
            onCreateSchool={quickActions.createSchool}
            onResendAdminInvite={resendAdminInvite}
            onAddAdmin={(schoolId) => setShowAdminModal({ visible: true, schoolId })}
            onOpenSchoolSettings={handleOpenSchoolSettings}
          />
        );

      case 'users':
        return (
          <UsersTab
            platformUsers={platformUsers}
            userActions={userActions}
            onManageSchools={() => handleTabChange('schools')}
          />
        );

      case 'feedback':
        return <FeedbackManagementScreen />;

      case 'settings':
        return (
          <SettingsTab
            settingsSection={settingsSection}
            onSettingsSectionChange={setSettingsSection}
            chatStreaming={chatStreaming}
            analysisStreaming={analysisStreaming}
            agenticChat={agenticChat}
            schools={schools}
            focusedSchoolId={focusedSchoolId}
            schoolActions={schoolActions}
            onCreateSchool={quickActions.createSchool}
            onPlatformDataChanged={loadPlatformData}
          />
        );

      default:
        return null;
    }
  };

  // Access control
  if (!user || !isSuperAdmin(user)) {
    return (
      <SafeAreaView className="flex-1" style={{ backgroundColor: ADMIN_COLORS.surface }}>
        <View style={{ padding: 16, paddingTop: 48 }}>
          <AdminSectionCard
            icon="admin-panel-settings"
            iconBg={ADMIN_COLORS.errorSoft}
            iconColor={ADMIN_COLORS.errorText}
            title={t('platformAdmin.accessRequired')}
            subtitle={t('platformAdmin.mustBeAdmin')}
          />
        </View>
      </SafeAreaView>
    );
  }

  const adminTabs: readonly AdminTab<PlatformAdminTabKey>[] = [
    { key: 'overview', label: t('platformAdmin.overview'), icon: 'dashboard' },
    { key: 'schools', label: t('platformAdmin.schools'), icon: 'school' },
    { key: 'users', label: t('platformAdmin.usersTab'), icon: 'group' },
    { key: 'feedback', label: 'Feedback', icon: 'feedback' },
    { key: 'settings', label: t('platformAdmin.settings'), icon: 'settings' },
  ];

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: ADMIN_COLORS.surface }}>
      <AdminPageHeader
        title={t('platformAdmin.title')}
        subtitle={t('platformAdmin.subtitle')}
        icon="shield"
        onBack={false}
      />

      <ScrollView
        className="flex-1"
        contentContainerStyle={{
          paddingBottom: Math.max(insets.bottom + 100, 120),
        }}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={ADMIN_COLORS.primary} />
        }
        showsVerticalScrollIndicator={false}
      >
        <View className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pt-5">
          {loading ? (
            <View className="flex-1 items-center justify-center p-10">
              <ActivityIndicator size="large" color={ADMIN_COLORS.primary} />
              <Text className="text-base mt-4" style={{ color: ADMIN_COLORS.text.secondary }}>
                {t('platformAdmin.loading')}
              </Text>
            </View>
          ) : (
            <>
              <View style={{ marginBottom: 20 }}>
                <AdminTabBar
                  tabs={adminTabs}
                  activeKey={activeTab}
                  onChange={handleTabChange}
                />
              </View>

              {/* Content */}
              <View className="p-5">
                {renderContent()}
              </View>
            </>
          )}
        </View>
      </ScrollView>

      {/* Modals */}
      {schoolSettingsMenu.visible && schoolSettingsMenu.school && (
        <SchoolActionsModal
          visible={schoolSettingsMenu.visible}
          school={schoolSettingsMenu.school}
          schoolActions={schoolActions}
          onOpenInSettings={focusSchoolInSettings}
        />
      )}
      <SchoolInviteAndPlanModals schoolActions={schoolActions} onChanged={loadPlatformData} />
    </SafeAreaView>
  );
}
